/**
 * 검색·정렬·태그·지난 강의 전환을 페이지 이동 없이 처리한다.
 *
 * 서버가 결과 영역(#academy-results) 마크업을 그대로 내려주므로 여기서는 받아서
 * 갈아끼우기만 한다. 검색 폼과 태그 줄은 건드리지 않아 입력 포커스와 태그 줄
 * 가로 스크롤 위치가 그대로 남는다.
 *
 * 주소는 history.pushState 로 맞춰 두므로 공유·새로고침·뒤로 가기가 평소와 같다.
 * 요청이 실패하면 원래대로 페이지를 이동시켜 기능이 멈추지 않게 한다.
 */
import { centerTagChip } from './academyTagRail';
import { syncDropdownLabels } from './initAcademyDropdown';

/**
 * 주소·REST 에 실리는 필터 이름.
 *
 * 축 파라미터(atr·alv·afm·arg)는 서버가 정한다 — 축이 늘면 PHP 한 곳만 고치면
 * 되도록 config.filterKeys 로 받아 온다. 예전 배포와 섞여 값이 없을 때만 아래를 쓴다.
 */
const DEFAULT_FILTER_KEYS = [
  'q',
  'view',
  'account',
  'atr',
  'alv',
  'afm',
  'arg',
  'atag',
  'sort',
  'past',
  'arv',
];

/**
 * 같은 term 을 가리키는 값인가.
 *
 * 한글 term 의 슬러그는 퍼센트 인코딩된 문자열(%ec%bb%a4…)이라, 라디오의 value 에는
 * 그 글자 그대로 들어 있지만 searchParams.get() 은 한 번 디코딩된 값을 돌려준다.
 * 양쪽을 같은 자리까지 풀어 놓고 비교한다 — 서버는 어느 쪽으로 와도 같은 슬러그로 읽는다.
 */
const sameTermValue = (a, b) => {
  const decode = (value) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value; // 온전치 않은 인코딩이면 원래 값끼리 비교한다
    }
  };

  return decode(a) === decode(b);
};

/**
 * 폼 값을 주소·REST 가 쓰는 모양으로 바꾼다.
 *
 * 계정 체크박스는 여러 개가 켜질 수 있어 account[] 로 올라온다. 주소에는 콤마로
 * 이어 한 칸(account=a,b)에 싣는다 — 서버도 같은 형태를 되읽는다.
 */
const formParams = (form) => {
  const params = new URLSearchParams(new FormData(form));
  const accounts = params.getAll('account[]');

  params.delete('account[]');

  if (accounts.length) {
    params.set('account', accounts.join(','));
  }

  return params;
};

const InitAcademyResults = () => {
  const results = document.getElementById('academy-results');
  const config = window.qrshopAcademy;

  if (!results || !config?.gridUrl) {
    return;
  }

  const filters = document.querySelector('.academy-filters');
  const form = document.querySelector('.academy-filters__form');
  const FILTER_KEYS = config.filterKeys?.length ? config.filterKeys : DEFAULT_FILTER_KEYS;
  const AXIS_KEYS = config.axisKeys?.length ? config.axisKeys : ['atr', 'alv', 'afm', 'arg'];
  // '전체' 가 푸는 조건. 보기 방식(view)·검토 모드(arv)는 '무엇으로 걸렀나'가 아니라
  // '무엇을 어떤 순서로 보고 있나'라서 남긴다 — 서버도 같은 것만 비운다(front-page.php).
  // sort·past 는 예전 주소로 들어온 경우를 위해 함께 남긴다.
  const KEPT_KEYS = ['view', 'sort', 'past', 'arv'];
  const RESET_KEYS = FILTER_KEYS.filter((key) => !KEPT_KEYS.includes(key));
  let pending = null;
  let currentWord = new URL(location.href).searchParams.get('q') || '';

  /**
   * 주소창에 넣을 URL — 빈 값은 뺀다(서버 규칙과 같다).
   *
   * 보기 방식(view)은 기본값이어도 뺴지 않는다. 빼면 그 주소를 받은 사람이 자기
   * 쿠키에 저장된 방식으로 보게 된다 — 링크는 보낸 사람이 보던 그대로여야 한다.
   */
  const pageUrl = (params) => {
    const url = new URL(config.homeUrl || location.pathname, location.href);

    FILTER_KEYS.forEach((key) => {
      const value = (params.get(key) || '').trim();

      if (value !== '') {
        url.searchParams.set(key, value);
      }
    });

    return url;
  };

  const requestUrl = (params, page) => {
    const url = new URL(config.gridUrl, location.href);

    FILTER_KEYS.forEach((key) => {
      const value = (params.get(key) || '').trim();
      if (value !== '') {
        url.searchParams.set(key, value);
      }
    });
    url.searchParams.set('mode', 'results');
    url.searchParams.set('page', String(page || 1));

    return url;
  };

  /**
   * 검색 폼을 주소에 실린 조건에 맞춘다.
   *
   * 결과 영역만 갈아끼우므로, 태그 칩이나 '필터 초기화'처럼 폼 밖에서 조건이 바뀌면
   * 폼은 예전 상태로 남는다. 목록은 전체인데 드롭다운에는 계정이 골라져 있는 식으로
   * 어긋나면 무엇으로 걸러진 목록인지 알 수 없게 된다.
   */
  const syncForm = (params) => {
    if (!form) {
      return;
    }

    const search = form.querySelector('input[name="q"]');

    if (search) {
      search.value = params.get('q') || '';
    }

    const accounts = (params.get('account') || '').split(',').filter(Boolean);

    form.querySelectorAll('input[name="account[]"]').forEach((box) => {
      box.checked = accounts.includes(box.value);
    });

    // 보기 방식. 예전 주소(sort=recent · past=1)로 들어온 경우도 서버와 같은 자리로 옮기고,
    // 주소에 없으면 서버가 알려준 값(쿠키에 기억된 것 또는 기본값)을 쓴다.
    let view = params.get('view') || '';

    if (!view) {
      if ((params.get('past') || '') !== '') {
        view = 'past';
      } else if (params.get('sort') === 'recent') {
        view = 'recent';
      } else {
        view = config.defaultView || 'recent';
      }
    }

    form.querySelectorAll('input[name="view"]').forEach((radio) => {
      radio.checked = radio.value === view;
    });

    // 축은 하나씩 고르는 라디오다. 주소에 값이 없으면 '고르지 않음' 항목(value="")이 켜진다.
    AXIS_KEYS.forEach((key) => {
      const value = params.get(key) || '';

      form.querySelectorAll(`input[name="${key}"]`).forEach((radio) => {
        radio.checked = sameTermValue(radio.value, value);
      });
    });

    syncDropdownLabels(form);
  };

  /**
   * '전체' 알약: 지금 걸린 조건이 하나도 없을 때만 켜지고, 링크도 지금 상태에서
   * 조건만 뺀 주소로 다시 만든다.
   *
   * 알약 줄은 결과 영역 밖이라 다시 그려지지 않는다. 여기서 맞춰 주지 않으면 축을
   * 골라도 '전체'가 켜진 채로 남아, 걸러진 목록을 보면서 전체를 보고 있다고 읽힌다.
   *
   * 강의 범위는 조건에서 뺀다 — '지난 강의'를 보다가 전체를 눌러도 계속 지난 강의를
   * 본다(서버가 만드는 링크와 같은 규칙이다).
   */
  const syncAll = (params) => {
    const pill = document.querySelector('.academy-filters__all');

    if (!pill) {
      return;
    }

    const url = pageUrl(params);
    const cleared = RESET_KEYS.every((key) => (params.get(key) || '').trim() === '');

    RESET_KEYS.forEach((key) => url.searchParams.delete(key));

    pill.href = url.toString();
    pill.classList.toggle('is-active', cleared);
  };

  /**
   * 추천어 칩: 활성 표시와 링크에 실린 검색어를 새 상태로 맞춘다.
   *
   * 칩은 검색어(q)를 바꾸는 링크다. 눌렸을 때 검색창에 그 말이 들어가는 것은
   * syncForm 이 맡는다 — 주소에 실린 q 를 폼에 그대로 옮기기 때문이다.
   */
  const syncTags = (params) => {
    const current = params.get('q') || '';

    document.querySelectorAll('.academy-tags__item').forEach((chip) => {
      const chipUrl = new URL(chip.href, location.href);
      const word = chipUrl.searchParams.get('q') || '';
      const next = pageUrl(params);

      if (word === '') {
        next.searchParams.delete('q');
      } else {
        next.searchParams.set('q', word);
      }

      chip.href = next.toString();
      chip.classList.toggle('is-active', word !== '' && word === current);

      if (word !== '' && word === current) {
        chip.setAttribute('aria-current', 'true');
      } else {
        chip.removeAttribute('aria-current');
      }
    });

    // 뒤로 가기처럼 클릭을 거치지 않고 검색어가 바뀌는 경우까지 가운데로 맞춘다.
    // 검색어가 그대로면 건드리지 않는다 — 사용자가 직접 굴려둔 위치를 뺏지 않기 위해서다.
    if (current !== currentWord) {
      currentWord = current;
      const active = document.querySelector('.academy-tags__item.is-active');

      if (active) {
        centerTagChip(active, 'smooth');
      }
    }
  };

  /**
   * 교체 + 짧은 페이드.
   *
   * View Transitions API 도 써봤지만 걷어냈다. 전환이 끝난 뒤에도 스냅샷 오버레이가
   * 포인터 이벤트를 가로채, 필터를 바꾼 직후 결과 영역의 링크·버튼이 눌리지 않았다
   * (클릭이 html 요소로 빠졌다). 불투명도만 건드리는 이 방식은 적중 판정에 영향이 없다.
   */
  const render = (html) => {
    results.innerHTML = html;
    results.classList.add('is-entering');
    requestAnimationFrame(() => results.classList.remove('is-entering'));
  };

  const navigate = async (url, { push = true } = {}) => {
    const params = new URL(url, location.href).searchParams;
    const target = pageUrl(params);

    pending?.abort();
    pending = new AbortController();
    results.classList.add('is-loading');
    results.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl(params, 1), {
        headers: config.nonce ? { 'X-WP-Nonce': config.nonce } : {},
        signal: pending.signal,
      });
      const data = await response.json();

      if (!response.ok || typeof data?.html !== 'string') {
        throw new Error('목록을 불러오지 못했습니다.');
      }

      if (push) {
        history.pushState({ academy: true }, '', target.toString());
      }

      render(data.html);
      syncTags(params);
      syncForm(params);
      syncAll(params);
    } catch (error) {
      if (error.name === 'AbortError') {
        return; // 더 새로운 요청이 이어받았다
      }
      location.assign(target.toString()); // 평소의 페이지 이동으로 되돌린다
      return;
    } finally {
      results.classList.remove('is-loading');
      results.removeAttribute('aria-busy');
    }
  };

  if (form) {
    form.addEventListener('submit', (event) => {
      event.preventDefault();

      navigate(pageUrl(formParams(form)));
    });
  }

  // 태그 칩·지난 강의 전환·필터 초기화는 모두 이 페이지를 가리키는 링크다.
  // 카드의 인스타 링크는 도메인이 달라 여기 걸리지 않는다.
  document.addEventListener('click', (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }

    const link = event.target.closest('a[href]');

    if (!link || (!link.closest('.academy-filters') && !results.contains(link))) {
      return;
    }

    const url = new URL(link.href, location.href);

    if (url.origin !== location.origin || url.pathname !== location.pathname) {
      return;
    }

    event.preventDefault();

    // 응답을 기다리지 않고 먼저 움직여 준다 — 누른 칩이 바로 가운데로 온다.
    if (link.classList.contains('academy-tags__item')) {
      centerTagChip(link, 'smooth');
    }

    navigate(url);
  });

  window.addEventListener('popstate', () => {
    navigate(location.href, { push: false });
  });

  // 첫 진입 상태도 기록해 둬야 뒤로 가기로 돌아왔을 때 이어진다.
  history.replaceState({ academy: true }, '', location.href);

  if (filters) {
    filters.dataset.academyAjax = '1';
  }
};

export default InitAcademyResults;
